import "@knocklabs/react/dist/index.css";
import { Analytics } from "@vercel/analytics/react";
import { Provider as JotaiProvider, useAtom } from "jotai";
import { log } from "next-axiom";
import type { AppType } from "next/app";
import bg from "public/images/occult.webp";
import { ErrorBoundary } from "react-error-boundary";
// biome-ignore lint/suspicious/noShadowRestrictedNames: <explanation>
import { Error } from "~/components/layouts/Error";
import { env } from "~/env";
import { OneAuthProviderToRuleThemAll as AuthProvider } from "~/providers/AuthProvider";
import { ModalProvider } from "~/providers/ModalProvider";
import { UserProvider } from "~/providers/UserProvider";
import "~/styles/globals.css";
import { Offline, Password, TooltipProvider } from "@sorcery/ui";
import { Cinzel, Enriqueta } from "next/font/google";
import Head from "next/head";
import { useEffect, useState } from "react";
import { devkeyJotai } from "~/state/configJotai";
import { api } from "~/utils/trpc";

/**
 * Nextjs font loading magic. Don't move this stuff or base styles dont get applied to react portals etc
 * https://github.com/vercel/next.js/issues/43674
 */
const enriquetta = Enriqueta({
  weight: ["400", "500", "600", "700"],
  variable: "--font-enriqueta",
});
const cinzel = Cinzel({
  weight: ["400", "500", "600", "700", "800"],
  variable: "--font-cinzel",
});

export const fonts = {
  cinzel,
  enriquetta,
};

// trigger deploy

const MyApp: AppType = ({ Component, pageProps }) => {
  const [devkey, setDevkey] = useAtom(devkeyJotai);
  const isOffline = env.NEXT_PUBLIC_OFFLINE === "1";
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    setIsReady(true);
  }, []);

  // Handle offline state
  if (isOffline) {
    return <Offline bg={bg} />;
  }

  const isPasswordProtected = env.NEXT_PUBLIC_VERCEL_ENV !== "production";
  const isValidDevKey = devkey === env.NEXT_PUBLIC_DEV_KEY;
  // Next, handle password protection state for staging builds
  if (isPasswordProtected) {
    if (!isReady) return null;
    // If the dev key is not valid, force password entry
    if (!isValidDevKey) {
      return <Password bg={bg} setV={setDevkey} />;
    }
  }

  return (
    <>
      <Head>
        {/* prevents safari auto zooming when inputs are clicked */}
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>

      <JotaiProvider>
        <ErrorBoundary
          FallbackComponent={Error}
          onError={(e) => {
            /**
             * Axiom trigger. Do not change this logged string
             */
            log.error("🔥 Client app crash in curiosa.io", {
              error: e,
              path:
                // biome-ignore lint/style/noRestrictedGlobals: <explanation>
                (window?.location.pathname ?? "") +
                // biome-ignore lint/style/noRestrictedGlobals: <explanation>
                (window?.location.search ?? ""),
              message: e.message ?? "No error message",
              // biome-ignore lint/style/noRestrictedGlobals: <explanation>
              user: (window as any)?.axiomUser ?? "anonymous",
              // pull the global window object skew state to account for FE triggered (rather than API failure) skew errors
              // biome-ignore lint/style/noRestrictedGlobals: <explanation>
              skewed: !!(window as any)?.skew,
            });
          }}
        >
          <AuthProvider>
            <UserProvider>
              <TooltipProvider>
                <ModalProvider>
                  <Component {...pageProps} />
                </ModalProvider>
              </TooltipProvider>
            </UserProvider>
          </AuthProvider>
        </ErrorBoundary>

        <Analytics />
      </JotaiProvider>
    </>
  );
};

export default api.withTRPC(MyApp);
